/**
 * T1704 — ONE fingerprint of a SET OF DESTINATIONS, written once and read by
 * both sides of the operator trigger.
 *
 * ⭐ WHAT IT IS FOR. Helen presses "Send prices to the portal" per account. The
 * kiosk then has to answer, on the same screen, whether what it sent is still
 * what the book says — and it has to answer that WITHOUT a second trip across
 * the seam. So the server records a hash of the set it sent, and the screen
 * hashes the set it is currently rendering; equal means nothing moved.
 *
 * ⛔⛔ ONE IMPLEMENTATION, IMPORTED BY BOTH, AND THAT IS THE WHOLE REASON THIS
 * FILE EXISTS RATHER THAN TWO LOCAL HELPERS. The writer is the server handler,
 * the reader is a Preact screen; two hashes that agree today and drift tomorrow
 * would show "Prices have changed since this was last sent." for ever, on a book
 * nobody touched, and every gate in the repo would stay green while it did. This
 * stack's largest recurring defect class is "a fix lands in one copy and the
 * sibling is left behind" — so there is no sibling.
 *
 * ⛔ THE THREE CONTRACT RULES ARE SPECIFIED, NOT LEFT TO TASTE, because each has
 * a wrong version that also leaves every gate green:
 *
 *  (a) SORTED NUMERICALLY ON `destinationId`, never on the name and never on
 *      arrival order. The two call sites read the same rows through DIFFERENT
 *      orderings (the server's picker read orders by name then id; the screen's
 *      book read orders by account name, then destination name, then the price
 *      row's own id), so arrival order is not stable between them — and two
 *      destinations that share a name tie-break differently in each. Sorting on
 *      the KEY is the only ordering both sides can agree on.
 *
 *  (b) EACH FIELD IS LENGTH-PREFIXED, `<len>:<value>`. `destinations.name` is
 *      operator-typed free text, so ANY plain delimiter collides: with a bare
 *      "|", the pair ("A|B", "C") and the pair ("A", "B|C") serialise to the
 *      same bytes and one rename becomes invisible. A length prefix is
 *      unambiguous whatever the operator types, including a name that is all
 *      digits or that contains the delimiter itself.
 *
 *  (c) FNV-1a (32-bit), hex. A CHANGE DETECTOR, not a security primitive: it
 *      answers "is this the same set I sent?" for an operator, and the worst
 *      case of a collision is a stale line on a screen, not a wrong price. It
 *      is used because it is short, dependency-free, and identical in the
 *      browser and in node.
 *
 * ⚠️ IT HASHES THE SET AND NOTHING ELSE — no figure is in scope here and none
 * may be added. The catalogue read this shadows deliberately carries no
 * `price_cents`, so editing a price CANNOT stale the portal; only the SET can.
 * A price folded into this hash would turn every price edit into a false
 * "the portal is out of date", and would put a figure into a value the screen
 * renders.
 */

/** The only shape this hashes: the KEY and the label, and no figure. */
export type FingerprintDestination = {
  destinationId: number;
  name: string;
};

/** `<len>:<value>` per field, concatenated. Injective for any operator text —
 *  a reader takes the digits up to the colon, then exactly that many characters,
 *  so no value can be mistaken for a delimiter. */
function encode(rows: readonly FingerprintDestination[]): string {
  return [...rows]
    .sort((a, b) => a.destinationId - b.destinationId)
    .map((r) => {
      const id = String(r.destinationId);
      return `${String(id.length)}:${id}${String(r.name.length)}:${r.name}`;
    })
    .join('');
}

/** FNV-1a, 32-bit, over UTF-16 code units. `Math.imul` because a plain `*` on
 *  the FNV prime overflows the float mantissa and silently stops mixing. */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * The fingerprint of one account's destination set.
 *
 * ⛔ ORDER-INDEPENDENT BY DESIGN, CONTENT-SENSITIVE BY DESIGN. Reordering the
 * same rows must not move it (the two reads order differently); a rename must,
 * and so must an EQUAL-CARDINALITY SWAP — one destination unpriced and another
 * priced, which is a routine edit of the book that a bare count would miss.
 */
export function fingerprintDestinations(rows: readonly FingerprintDestination[]): string {
  return fnv1a(encode(rows));
}

/**
 * The `app_meta` key the send is recorded under, per account.
 *
 * ⛔ BUILT HERE FOR THE SAME REASON THE HASH IS. The server writes this key and
 * the screen reads it; a prefix that drifted by one character would make every
 * lookup miss, and the screen would report "Not sent to the portal yet." for
 * ever with no error anywhere. One builder, both sides.
 */
export function portalDestinationsSentKey(accountId: number): string {
  return `portal_destinations_sent:${String(accountId)}`;
}
